/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import {normalizeSetBundleProduct} from '@salesforce/retail-react-app/app/utils/product-utils'

// Components
import {Box, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useCategory,
    useShopperCustomersMutation,
    useCustomerId,
    useShopperBasketsMutationHelper,
    useCustomerOrders,
    useCustomerType
} from '@salesforce/commerce-sdk-react'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useSearchParams, useVariant} from '@salesforce/retail-react-app/app/hooks'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '../../hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useCustomerGroups from '../../hooks/use-customer-groups'
// Project Components
import ProductView from '../../components/product-view'

import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// constant
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE
} from '@salesforce/retail-react-app/app/constants'
import {useParams, useLocation} from 'react-router-dom'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useWishList} from '../../hooks/use-wish-list'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import PDComponent from '../../components/page-designer/pd-component'
import LoadingSpinner from '../../components/loading-spinner'
import useMonetate from '../../hooks/use-monetate'
import OptiversalLinks from './partials/optiversal-links'
import MirelzScript from './partials/mirelz-script'
import useTagManager from '../../hooks/use-tag-manager'
import usePDP from '../../hooks/use-pdp'
import {getTotalQuantity} from '../../utils/basket-utils'
import {useGlobaleContext} from '../../contexts/globale'
import {getPriceData} from '../../utils/product-utils'
import Schema from './partials/schema'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useSiteContext} from '../../contexts/site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {isHydrated, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {YR_HOME_HREF, KS_HOME_HREF, ENGAGEMENT_PRODUCTS} from '../../constants'
import {useQueryClient} from '@tanstack/react-query'
import {logBasketErrorToSsr} from '../../utils/log-basket-error-to-ssr'
import {scheduleIdleAnalytics} from '../../utils/common-utils'

const ProductDetail = () => {
    const {formatMessage} = useIntl()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {countryISO, currencyCode} = useGlobaleContext()
    const toast = useToast()
    const queryClient = useQueryClient()
    const customerId = useCustomerId()

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const {isRegistered} = useCustomerType()

    /****************************** Basket *********************************/
    const {isLoading: isBasketLoading, data: basket} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {data: customerGroupIds} = useCustomerGroups()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {configOptiversal} = commerceAPI.optiversal || {}

    var enabledRelatedLinksPDP = false
    var maxLinksPDP = 10

    try {
        const jsonConfig = JSON.parse(configOptiversal)
        enabledRelatedLinksPDP = jsonConfig.enabledRelatedLinksPDP
        maxLinksPDP = jsonConfig.maxLinksPDP
    } catch (e) {
        console.log(e.message)
    }

    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    /*************************** Product Detail and Category ********************/
    const {productId} = useParams()
    const pdpRef = useRef()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const {data: PDP, isLoading: isProductLoading} = usePDP({
        product: {
            id: productId,
            perPricebook: true,
            expand: [
                'availability',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
                'set_products',
                'page_meta_tags',
                'bundled_products'
            ],
            allImages: true,
            c_selectedCountry: countryISO,
            c_selectedCurrency: currencyCode,
            c_isPDP: true
        },
        pageDesigner: {
            aspectTypeId: 'pdp'
        },
        customerGroupIds
    })
    const {product, pageDesigner} = PDP || {}
    const {data: customer} = useCurrentCustomer()
    const {buildUrl} = useMultiSite()
    const {isYR} = useSiteContext()
    const navigate = useNavigation()

    if (!isProductLoading && !product && !isServer) {
        // Redirect to home if product is not found
        navigate(KS_HOME_HREF)
    }

    if (isHydrated() && customerGroupIds && !isServer) {
        const showPDP =
            product?.c_pdpRestrictionEnabled && product?.c_pdpRestrictionGroups
                ? product?.c_pdpRestrictionGroups?.find(
                      (group) => customerGroupIds?.customerGroupIds.indexOf(group) > -1
                  )
                : true

        if (!showPDP) {
            const path = buildUrl(isYR ? YR_HOME_HREF : KS_HOME_HREF)
            navigate(path)
        }
    }

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const categoryEnabled = !!product?.c_primaryCategory && !isServer
    const {data: category} = useCategory(
        {
            parameters: {
                id: product?.c_primaryCategory,
                levels: 1
            }
        },
        {enabled: categoryEnabled}
    )

    /****************************** Sets *********************************/
    const isProductASet = product?.type?.set
    const comboProduct = isProductASet ? normalizeSetBundleProduct(product) : {}

    const variant = useVariant(product)

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const isItemInWishlist = !!wishlist?.customerProductListItems?.find(
        (i) => i.productId === variant?.productId || i.productId === product?.id
    )
    const [productAddedToWishlist, setProductAddedToWishlist] = useState(false)
    const [wishlistSpinner, setWishlistSpinner] = useState(false)
    const [componentProps, setComponentProps] = useState({})

    // Polling function to make sure customerId & listId are available
    const customerIdRef = useRef(null)
    const listIdRef = useRef(null)

    useEffect(() => {
        if (customerIdRef) {
            customerIdRef.current = customerId
        }
    }, [customerId])
    useEffect(() => {
        if (listIdRef) {
            listIdRef.current = wishlist?.id || null
        }
    }, [wishlist])

    const waitForRefs = async () => {
        const maxRetries = 10
        const interval = 100
        let retries = 0

        return new Promise((resolve, reject) => {
            const checkRefs = () => {
                const listId = listIdRef.current
                if (customerIdRef.current === customerId && listId) {
                    resolve()
                } else if (retries < maxRetries) {
                    retries++
                    setTimeout(checkRefs, interval)
                } else {
                    reject(new Error('Timeout waiting for customerId and listId'))
                }
            }
            checkRefs()
        })
    }

    const handleAddToWishlist = async (product, variant, quantity) => {
        setWishlistSpinner(true)
        await waitForRefs()
        const customerId = customerIdRef.current
        const listId = listIdRef.current

        if (isItemInWishlist) {
            const itemId = wishlist.customerProductListItems.find(
                (i) => i.productId === product.id
            ).id

            try {
                await deleteCustomerProductListItem.mutate(
                    {
                        body: {},
                        parameters: {
                            customerId,
                            listId,
                            itemId
                        }
                    },
                    {
                        onError: () => {
                            toast({
                                title: formatMessage(API_ERROR_MESSAGE),
                                status: 'error'
                            })
                            setWishlistSpinner(false)
                        },
                        onSuccess: () => {
                            setProductAddedToWishlist(false)
                            tagManager.wishlistInteraction(
                                category?.name || '',
                                product?.id,
                                product?.name,
                                (wishlist?.customerProductListItems?.length || 0) + 1,
                                'remove'
                            )
                            try {
                                tagManager.trackGA4Event({
                                    eventName: 'remove_from_wishlist',
                                    itemsAdded: [
                                        {
                                            product: product
                                        }
                                    ],
                                    variant: {pid: product?.id},
                                    method: 'pdp',
                                    currency: currencyCode,
                                    listName: 'pdp',
                                    listId: 'pdp'
                                })
                            } catch (e) {
                                logger.error('GA4 remove_from_wishlist failed', {
                                    namespace: 'ProductDetail.GA4',
                                    additionalProperties: {error: e}
                                })
                            }
                        },
                        onSettled: () => {
                            setWishlistSpinner(false)
                        }
                    }
                )
            } catch (error) {
                setWishlistSpinner(false)
                logger.error('Error adding product to wishlist', {
                    namespace: 'ProductDetail.handleAddToWishlist',
                    additionalProperties: {error, productId: variant?.productId || product?.id}
                })
            }
        } else {
            try {
                await createCustomerProductListItem.mutate(
                    {
                        parameters: {
                            listId,
                            customerId
                        },
                        body: {
                            // NOTE: API does not respect quantity, it always adds 1
                            quantity,
                            productId: variant?.productId || product?.id,
                            public: true,
                            priority: 1,
                            type: 'product'
                        }
                    },
                    {
                        onSuccess: () => {
                            setWishlistSpinner(false)
                            setProductAddedToWishlist(true)

                            // EXISTING EVENT (UNCHANGED)
                            tagManager.wishlistInteraction(
                                category?.name || '',
                                product?.id,
                                product?.name,
                                (wishlist?.customerProductListItems?.length || 0) + 1,
                                'add'
                            )

                            // NEW GA4 EVENT
                            try {
                                tagManager.trackGA4Event({
                                    eventName: 'add_to_wishlist',
                                    itemsAdded: [
                                        {
                                            product: product
                                        }
                                    ],
                                    variant: {pid: product?.id},
                                    method: 'pdp',
                                    currency: currencyCode,
                                    listName: 'pdp',
                                    listId: 'pdp'
                                })
                            } catch (e) {
                                logger.error('GA4 add_to_wishlist failed', {
                                    namespace: 'ProductDetail.GA4',
                                    additionalProperties: {error: e}
                                })
                            }
                        },
                        onError: () => {
                            setWishlistSpinner(false)
                        },
                        onSettled: () => {
                            setWishlistSpinner(false)
                        }
                    }
                )
            } catch (error) {
                setWishlistSpinner(false)
                logger.error('Error adding product to wishlist', {
                    namespace: 'ProductDetail.removeItemFromWishlist',
                    additionalProperties: {error, productId: variant?.productId || product?.id}
                })
            }
        }
    }

    /**************** Add to Bag ****************/
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const handleAddToCart = async (
        productSelectionValues,
        driveRecipeID,
        engravingChecksum,
        engravingGtmObject
    ) => {
        try {
            const productItems = productSelectionValues.map(({product, variant, quantity}) => {
                const base = {
                    productId: variant.addByID
                        ? variant.productId?.indexOf('_') > -1
                            ? variant.productId?.split('_')[1] // 360 mirelz id is ks_[productID]
                            : variant.productId
                        : variant.productId,
                    price: variant.price,
                    c_driveRecipeID: driveRecipeID,
                    c_engravingChecksum: engravingChecksum,
                    c_engravingGtmObject: engravingGtmObject
                        ? JSON.stringify(engravingGtmObject)
                        : null,
                    quantity
                }
                const analytics = variant?.c_analyticsAttributes || product?.c_analyticsAttributes
                if (analytics) {
                    base.c_analyticsAttributes = analytics
                }
                return base
            })

            const basketResponse = await addItemToNewOrExistingBasket(productItems)

            einstein.sendAddToCart(productItems)

            // If the items were successfully added, set the return value to be used
            // by the Add to Bag modal.
            return {productSelectionValues: productSelectionValues, basketResponse: basketResponse}
        } catch (error) {
            const errResponse = error?.response ? await error.response.json() : null
            logger.error('ATCXL Error adding product to basket', {
                namespace: 'ProductDetail.handleAddToCart',
                additionalProperties: {error, errResponse, productId, basketId: basket?.basketId}
            })
            logBasketErrorToSsr('ProductDetail.handleAddToCart', {
                error,
                errResponse,
                productId,
                basketId: basket?.basketId
            })
            if (errResponse?.statusCode === '403') {
                queryClient.invalidateQueries()
            } else {
                showError(error)
            }
        }
    }

    /**************** Product Set Handlers ****************/
    const handleProductSetAddToCart = async (selectedVariants) => {
        // Get all the selected products, and pass them to the addToCart handler which
        // accepts an array.
        return await handleAddToCart(selectedVariants)
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (!product) return

        const productViewSchedule = scheduleIdleAnalytics(() => {
            if (product.type?.set) {
                einstein.sendViewProduct(product)
                product.setProducts.forEach((child) => {
                    try {
                        einstein.sendViewProduct(child)
                    } catch (err) {
                        logger.error('Einstein sendViewProduct error', {
                            namespace: 'ProductDetail.useEffect',
                            additionalProperties: {error: err, child}
                        })
                    }
                    activeData.sendViewProduct(category, child, 'detail')
                })
                return
            }

            try {
                einstein.sendViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product}
                })
            }
            activeData.sendViewProduct(category, product, 'detail')
        })

        return () => productViewSchedule?.cancel?.()
    }, [product])

    const orderSearchParams = useSearchParams({limit: 1, offset: 0})
    const {limit, offset} = orderSearchParams[0]
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient() && !!customerId && customer?.customerType === 'registered'}
    )

    useEffect(() => {
        if (!isOrdersLoading && pdpRef) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            pdpRef.current?.setAttribute('data-last-order-date', orders?.[0]?.creationDate || 'n/a')
        }
    }, [isOrdersLoading, pdpRef])

    useEffect(() => {
        if (!isWishlistLoading && pdpRef) {
            pdpRef.current?.setAttribute(
                'data-wishlist-length',
                wishlist?.customerProductListItems?.length || 0
            )
        }
    }, [isWishlistLoading, pdpRef])

    useEffect(() => {
        if (!isBasketLoading && pdpRef) {
            pdpRef.current?.setAttribute(
                'data-basket-items-length',
                basket?.productItems?.length ? JSON.stringify(basket?.productItems) : '[]'
            )
            pdpRef.current?.setAttribute(
                'data-basket-gift-length',
                basket?.giftCertificateItems?.length
                    ? JSON.stringify(basket?.giftCertificateItems)
                    : '[]'
            )
        }
    }, [isBasketLoading, pdpRef, basket])

    useEffect(() => {
        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            pdpRef.current?.setAttribute(
                'data-is-registered',
                customer?.customerType === 'registered'
            )
            window.birthdayMonth = customer?.c_birthdayMonth
            window.monetatePhilantropyShopper = customer?.c_monetatePhilantropyShopper
        }
    }, [customer?.customerType, customer?.email, pdpRef])
    const triggerMonetateEvent = () => {
        var breadCrumb = product?.c_breadcrumbs
            ? product?.c_breadcrumbs?.map((b) => b.htmlValue).join('/') + '/'
            : ''
        breadCrumb = 'Home/' + breadCrumb + product.name
        if (breadCrumb) {
            window.mtBreadcrumb = breadCrumb.split('/')
            monetate.monetateTrackPage(
                'product',
                breadCrumb.split('/'),
                pdpRef.current?.getAttribute('data-basket-items-length')
                    ? JSON.parse(pdpRef.current?.getAttribute('data-basket-items-length'))
                    : [],
                pdpRef.current?.getAttribute('data-is-registered') === 'true',
                pdpRef.current?.getAttribute('data-wishlist-length') || 0,
                [product],
                null,
                null,
                pdpRef.current?.getAttribute('data-basket-gift-length')
                    ? JSON.parse(pdpRef.current?.getAttribute('data-basket-gift-length'))
                    : []
            )
            window.monetateRef = product?.id
        }
    }

    useEffect(() => {
        if (product?.id) {
            let monetateSchedule

            /** NOTE: Not a ReactJS practice, but we need to make sure that the order and the wishlist data is completely fetched and new product is available. **/
            var CONTROL_INTERVAL_MODULE = setInterval(function () {
                if (
                    (pdpRef.current?.getAttribute('data-last-order-date') ||
                        customer?.customerType !== 'registered') &&
                    (pdpRef.current?.getAttribute('data-wishlist-length') ||
                        customer?.customerType !== 'registered') &&
                    pdpRef.current?.getAttribute('data-basket-items-length') !== '' &&
                    pdpRef.current?.getAttribute('data-is-registered')
                ) {
                    monetateSchedule = scheduleIdleAnalytics(triggerMonetateEvent)
                    clearInterval(CONTROL_INTERVAL_MODULE)
                }
            }, 1000) // check for every 1s

            return () => {
                clearInterval(CONTROL_INTERVAL_MODULE)
                monetateSchedule?.cancel?.()
            }
        }
    }, [product?.id])

    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}

    var changeUpProductID = null

    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const totalQuantity = getTotalQuantity(basket, changeUpProductID)

    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const [dataLayerLoaded, setDataLayerLoaded] = useState(false)
    const globalDataScheduleRef = useRef(null)

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isBasketLoading &&
            !globalDataLoaded
        ) {
            globalDataScheduleRef.current?.cancel?.()

            globalDataScheduleRef.current = scheduleIdleAnalytics(() => {
                tagManager.globalDataLoad(
                    'PDP',
                    category?.name,
                    totalQuantity,
                    basket?.productTotal,
                    customer,
                    city,
                    country,
                    state
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }
    }, [customer?.customerType, customer?.email, isBasketLoading, cookie])

    useEffect(() => {
        setGlobalDataLayerLoaded(false)
        setDataLayerLoaded(false)
    }, [product?.c_masterVariantID])

    useEffect(() => {
        let dataLayerSchedule

        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (
            product?.name &&
            globalDataLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            var breadCrumb = product?.c_breadcrumbs
                ? product?.c_breadcrumbs?.map((b) => b.htmlValue).join('/') + '/' + product?.name
                : ''
            dataLayerSchedule = scheduleIdleAnalytics(() => {
                try {
                    tagManager.dataLayerLoad(
                        'PDP',
                        isRegistered,
                        breadCrumb,
                        countryISO,
                        currencyCode,
                        customer,
                        city,
                        country,
                        state,
                        null,
                        null,
                        'other'
                    )
                } catch (e) {
                    tagManager.dataLayerLoad(
                        'PDP',
                        isRegistered,
                        breadCrumb,
                        null,
                        null,
                        customer,
                        city,
                        country,
                        state,
                        null,
                        null,
                        'other'
                    )
                }
                setDataLayerLoaded(true)
            })
        }

        return () => dataLayerSchedule?.cancel?.()
    }, [product?.c_masterVariantID, globalDataLoaded, customer?.customerType, customer?.email])

    const location = useLocation()

    useEffect(() => {
        window.fbEventID = Math.random().toString()
        const locationProps = location?.state?.trackingProps
        const trackingProps =
            locationProps || (typeof window !== 'undefined' && window.__lastComponentProps) || {}
        const productViewSchedule = scheduleIdleAnalytics(() => {
            tagManager.productView(
                product,
                null,
                product?.c_isEngravable,
                false,
                false,
                trackingProps
            )
            if (localStorage.getItem('changingSwatch') === 'true') {
                var breadCrumb = product?.c_breadcrumbs
                    ? product?.c_breadcrumbs?.map((b) => b.htmlValue).join('/') + '/'
                    : ''
                breadCrumb = 'Home/' + breadCrumb + product.name
                tagManager.productClick(
                    'pdp',
                    product,
                    product,
                    undefined,
                    breadCrumb,
                    category,
                    'pdp',
                    undefined
                )
                localStorage.removeItem('changingSwatch')
            }
        })

        setComponentProps(trackingProps)

        return () => productViewSchedule?.cancel?.()
    }, [product?.id])
    const priceData = useMemo(() => {
        return getPriceData(product, 1)
    }, [product, 1])

    const handleAddToCartWrapperForSets = async (
        variant,
        quantity,
        driveRecipeID,
        engravingChecksum,
        engravingGtmObject,
        isChild,
        childProductSelection
    ) => {
        if (isChild) {
            return await handleAddToCart([{variant, quantity}])
        }

        const selectedVariants = Object.values(childProductSelection).map((childProduct) => {
            const variantPrice = getPriceData(childProduct.selectedSku).currentPrice
            const variant = childProduct.selectedSku

            return {
                variant: {...variant, price: variantPrice},
                quantity: childProduct.quantity,
                price: variantPrice
            }
        })

        return await handleProductSetAddToCart(selectedVariants)
    }

    const isEngagementProduct = ENGAGEMENT_PRODUCTS?.some(
        (id) => id === product?.c_subclassCode?.toLowerCase()
    )
    return (
        <Box
            className="sf-product-detail-page"
            ref={pdpRef}
            data-wishlist-length=""
            data-last-order-date=""
            data-basket-items-length=""
            data-basket-gift-length=""
            data-is-registered=""
            layerStyle="fullPage"
            data-testid="product-details-page"
            background="white"
            marginBottom={{base: '30px', lg: '50px'}}
        >
            <Schema product={product} selectedVariant={variant} />
            <Helmet>
                <title>{product?.pageTitle || product?.name}</title>
                {product?.pageMetaTags?.map((tag) =>
                    tag.id.startsWith('og:') ? (
                        <meta
                            key={tag.id}
                            property={tag.id}
                            content={
                                tag.id === 'og:url'
                                    ? product?.slugUrl
                                    : tag.id === 'og:image'
                                    ? product?.c_images?.mediumImage
                                    : tag.value
                            }
                        />
                    ) : (
                        <meta key={tag.id} name={tag.id} content={tag.value} />
                    )
                )}
                {product?.slugUrl && <link rel="canonical" href={product?.slugUrl}></link>}
            </Helmet>
            {wishlistSpinner && <LoadingSpinner position="fixed" top="auto" />}
            <Stack spacing={16}>
                {isProductASet ? (
                    <Fragment>
                        <ProductView
                            product={product}
                            addToCart={handleAddToCartWrapperForSets}
                            dataLayerLoaded={dataLayerLoaded}
                            isEngagementProduct={isEngagementProduct}
                            addToWishlist={handleAddToWishlist}
                            isItemInWishlist={isItemInWishlist}
                            basket={basket}
                            productAddedToWishlist={productAddedToWishlist}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            wishlist={wishlist}
                            countryISO={countryISO}
                            isSet={true}
                            comboProduct={comboProduct}
                        />
                    </Fragment>
                ) : (
                    <Fragment>
                        <ProductView
                            product={product}
                            isEngagementProduct={isEngagementProduct}
                            addToCart={(
                                variant,
                                quantity,
                                driveRecipeID = null,
                                engravingChecksum = null,
                                engravingGtmObject = null
                            ) =>
                                handleAddToCart(
                                    [{product, variant, quantity}],
                                    driveRecipeID,
                                    engravingChecksum,
                                    engravingGtmObject
                                )
                            }
                            basket={basket}
                            dataLayerLoaded={dataLayerLoaded}
                            addToWishlist={handleAddToWishlist}
                            isItemInWishlist={isItemInWishlist}
                            productAddedToWishlist={productAddedToWishlist}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            wishlist={wishlist}
                            countryISO={countryISO}
                            analyticsProps={componentProps || {}}
                        />
                    </Fragment>
                )}
            </Stack>
            <MirelzScript
                scriptId="mzDefault"
                product={product}
                customer={customer}
                wishlist={wishlist}
                priceData={priceData}
                addToCart={(items) => handleAddToCart(items)}
            />
            <MirelzScript
                scriptId="mz360"
                product={product}
                customer={customer}
                wishlist={wishlist}
                priceData={priceData}
                addToCart={(items) => handleAddToCart(items)}
            />
            {!isProductLoading && pageDesigner && (
                <Box backgroundColor="white">
                    <PDComponent parent={true} page={pageDesigner?.data?.[0]} product={product} />
                </Box>
            )}
            <Box
                backgroundColor="white"
                display={isProductASet || isEngagementProduct ? 'none' : 'block'}
                className="reviews-wrapper"
            >
                <Box
                    id="bv-product-reviews"
                    data-bv-show="reviews"
                    backgroundColor="white"
                    alignItems="center"
                    maxWidth="1180px"
                    margin="20px auto"
                    paddingX={{base: '15px', lg: '40px'}}
                    sx={{
                        'img, svg, video, canvas, audio, iframe, embed, object': {
                            display: 'initial'
                        }
                    }}
                    data-bv-product-id={product?.id}
                />
            </Box>
            <Box backgroundColor="white" className="optiversal-link-wrapper">
                {product?.id && enabledRelatedLinksPDP && (
                    <OptiversalLinks query={product?.id} maxLinks={maxLinksPDP} queryType="sku" />
                )}
            </Box>
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object
}

export default ProductDetail
